import { Box, Stack } from '@mantine/core';
import { UserRole } from '@repo/common/enums/UserRole';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { IReport } from '../../api';
import { useReportList } from '../../api/hooks/report';
import { useSuspenseFeatureAccess } from '../../api/hooks/workspace/useSuspenseFeatureAccess';
import { EducationModal } from '../../components/EducationModal/EducationModal';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import { TableV2 } from '../../components/TableV2';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';
import { WidgetSize } from '../../interfaces';
import { useFeatureFlags } from '../../utils/featureFlags';
import { checkFeatureAccess } from '../../utils/featureFlags/featureFlagUtils';
import { useStyles } from '../TeamCatalogPage/TeamCatalogPage.styles';
import { useActions, useColumns } from './AnalyticsListPage.hooks';
import AnalyticsReportCard from './AnalyticsReportCard';
import type { CachedReportView } from './AnalyticsReportCardLoading';
import AnalyticsReportCardLoading from './AnalyticsReportCardLoading';

const COLUMN_VISIBILITY = {
	catalogServerType: 'reports',
	catalogType: 'report',
} as const;

const DEFAULT_SEARCH_PARAMS = {
	type: 'analytics',
} as const;

const QUICK_ACTIONS = ['actions::delete'] as const;

const FEATURE_BASED_FILTERS = [
	{ access: 'aiAccess', type: 'analytics_default_ai' },
	{ access: 'dqsAccess', type: 'data_quality' },
	{ access: 'automationsAccess', type: 'analytics_default_automation' },
	{ access: 'monitoringAccess', type: 'analytics_default_monitor' },
];

const LOADING_STATE_REPORT_VIEW: CachedReportView[] = Array.from({
	length: 8,
}).map(() =>
	Array.from({ length: 8 }).map(() => ({
		size: WidgetSize.MEDIUM,
	}))
);

function AnalyticsListPage() {
	const { classes, theme } = useStyles();
	const navigate = useNavigate();
	const columns = useColumns();
	const actions = useActions();
	const { analyticsAccess } = useSuspenseFeatureAccess();
	const { reportCardsView, aiImpactDashboard } = useFeatureFlags();
	const featureAccess = useSuspenseFeatureAccess();

	const selectFunction = useMemo(
		() => (data: any) =>
			data.results
				.filter(
					(report: IReport) =>
						!FEATURE_BASED_FILTERS.some(
							(filter) =>
								!checkFeatureAccess(filter.access, featureAccess) &&
								report.type === filter.type
						)
				)
				.sort((a: IReport, b: IReport) => a.name.localeCompare(b.name)),
		[featureAccess]
	);

	const { data: reports, isLoading } = useReportList({
		filters: {
			type: 'analytics',
		},
		options: {
			enabled: reportCardsView,
			select: selectFunction,
		},
	});

	const handleClick = useCallback(
		(report: IReport) => {
			// Navigate to AI Analytics page if report type is d_ai and feature flag is enabled
			if (aiImpactDashboard && report.type === 'd_ai') {
				navigate('/analytics/ai-analytics');
			} else {
				navigate(`/analytics/${report.id}`);
			}
		},
		[navigate, aiImpactDashboard]
	);

	const onCellClick = useCallback(
		(cell: { column: ExtendedDataTableColumn<IReport>; record: IReport }) => {
			if (cell.column.navigate !== false) {
				handleClick(cell.record);
			}
		},
		[handleClick]
	);

	return (
		<FeatureGuard
			featureName="Analytics"
			description="Understand workspace usage, important KPIs, and more with analytics."
			v1AllowedRoles={[UserRole.ADMIN, UserRole.EDITOR]}
			v2Permission="Analytics.Read"
			isFeatureIncludedInPlan={analyticsAccess ?? false}
			isAlwaysIncludedFeature={false}
		>
			{reportCardsView && (
				<Stack spacing="sm" px="xl" data-testid="analytics-list">
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
							gap: theme.spacing.md,
							width: '100%',
						}}
						pb="xl"
					>
						{!isLoading &&
							reports?.map((report: IReport) => (
								<AnalyticsReportCard
									key={report.id}
									report={report}
									onClick={() => handleClick(report)}
								/>
							))}
						{isLoading &&
							LOADING_STATE_REPORT_VIEW.map((r, index) => (
								<AnalyticsReportCardLoading
									// eslint-disable-next-line react/no-array-index-key
									key={index}
									reportView={r}
								/>
							))}
					</Box>
				</Stack>
			)}
			{!reportCardsView && (
				<Stack className={classes.wrapper} data-testid="analytics-list">
					<TableV2<IReport>
						columnVisibility={COLUMN_VISIBILITY}
						pluralTypeString="reports"
						withCheckbox={true}
						withInteractiveHeader
						withSearch
						defaultSort={null}
						withCsvExport
						columns={columns}
						withActions={actions}
						withQuickActions={QUICK_ACTIONS}
						usePaginationList={useReportList}
						defaultRequiredSearchParams={DEFAULT_SEARCH_PARAMS}
						onCellClick={onCellClick}
					/>
				</Stack>
			)}
			{aiImpactDashboard && (
				<EducationModal modalKey="education-modal-of-secoda-ai-analytics" />
			)}
		</FeatureGuard>
	);
}

export default AnalyticsListPage;
